import { useCallback, useRef, useState } from 'react';
import { turnstileSiteKey } from '../config';

/**
 * Cloudflare Turnstile hook for SPAs.
 *
 * Exposes `turnstileStatus` so pages can gate their submit button:
 *   'idle'    — widget not yet mounted / no site key
 *   'pending' — script loading or challenge running (for suspicious traffic,
 *               Cloudflare may show an interactive challenge at this point)
 *   'ready'   — token buffered and ready; safe to call getTurnstileToken()
 *   'error'   — challenge permanently failed after retries
 *
 * Widget callbacks capture `localId` at render-time so stale callbacks from a
 * removed widget (React StrictMode mount→unmount→remount) are silently ignored.
 *
 * @see https://developers.cloudflare.com/turnstile/get-started/client-side-rendering/
 */

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const MAX_ERROR_RETRIES = 2;

let scriptPromise = null;

/** Create an Error that pages can distinguish from non-Turnstile errors. */
function makeTurnstileError(msg) {
  const e = new Error(msg);
  e.isTurnstileError = true;
  return e;
}

function loadTurnstileScript() {
  if (typeof window === 'undefined') {
    return Promise.reject(makeTurnstileError('Turnstile requires a browser environment'));
  }
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[src*="challenges.cloudflare.com/turnstile"]');
    if (existing) {
      if (window.turnstile) { resolve(); return; }
      existing.addEventListener('load', resolve);
      existing.addEventListener('error', () => {
        scriptPromise = null;
        reject(makeTurnstileError('Failed to load Turnstile script'));
      });
      return;
    }
    const s = document.createElement('script');
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = resolve;
    s.onerror = () => {
      scriptPromise = null;
      reject(makeTurnstileError('Failed to load Turnstile script'));
    };
    document.head.appendChild(s);
  });
  return scriptPromise;
}

// turnstile.ready() must NOT be used with async/defer scripts — our onload
// handler guarantees window.turnstile is available when the promise resolves.
function awaitTurnstileReady() {
  return loadTurnstileScript();
}

export function useTurnstile() {
  const containerRef  = useRef(null);
  const widgetIdRef   = useRef(null);
  const bufferedToken = useRef(null);
  const pendingRef    = useRef([]);   // queued getTurnstileToken() waiters
  const errorCount    = useRef(0);    // consecutive Cloudflare errors for current widget

  // Reactive status so pages can drive button state without polling.
  const [turnstileStatus, setTurnstileStatus] = useState(
    turnstileSiteKey ? 'pending' : 'idle'
  );
  const [turnstileInteractive, setTurnstileInteractive] = useState(false);

  // ── helpers ────────────────────────────────────────────────────────────────

  const rejectAllPending = useCallback((err) => {
    const queue = pendingRef.current;
    pendingRef.current = [];
    queue.forEach((p) => {
      window.clearTimeout(p.timeoutId);
      p.reject(err);
    });
  }, []);

  /** Deliver a fresh token — resolves a pending waiter or buffers for later. */
  const deliverToken = useCallback((token) => {
    errorCount.current = 0;
    setTurnstileStatus('ready');
    const queue = pendingRef.current;
    if (queue.length) {
      const first = queue.shift();
      window.clearTimeout(first.timeoutId);
      first.resolve(token);
    } else {
      bufferedToken.current = token;
    }
  }, []);

  // ── widget lifecycle ────────────────────────────────────────────────────────

  const removeWidget = useCallback(() => {
    const id = widgetIdRef.current;
    widgetIdRef.current = null;
    bufferedToken.current = null;
    errorCount.current = 0;
    setTurnstileInteractive(false);
    setTurnstileStatus(turnstileSiteKey ? 'pending' : 'idle');
    if (id != null && window.turnstile) {
      try { window.turnstile.remove(id); } catch (_) { /* ignore */ }
    }
    rejectAllPending(makeTurnstileError('Turnstile widget removed'));
  }, [rejectAllPending]);

  const mountWidgetIfNeeded = useCallback(() => {
    if (!turnstileSiteKey || !containerRef.current || widgetIdRef.current != null) {
      return Promise.resolve();
    }
    setTurnstileStatus('pending');
    return awaitTurnstileReady().then(() => {
      // Guard: may have unmounted while script was loading (StrictMode).
      if (!containerRef.current || widgetIdRef.current != null) return;

      const api = window.turnstile;
      if (!api || typeof api.render !== 'function') return;

      // Capture widgetId so each callback only acts on *its own* widget instance.
      let localId;

      localId = api.render(containerRef.current, {
        sitekey:    turnstileSiteKey,
        theme:      'dark',
        size:       'normal',
        language:   'en',
        appearance: 'interaction-only',

        callback: (token) => {
          if (widgetIdRef.current !== localId) return; // stale — ignore
          setTurnstileInteractive(false);
          deliverToken(token);
        },

        'before-interactive-callback': () => {
          if (widgetIdRef.current !== localId) return; // stale — ignore
          setTurnstileInteractive(true);
        },

        'after-interactive-callback': () => {
          if (widgetIdRef.current !== localId) return; // stale — ignore
          setTurnstileInteractive(false);
        },

        'error-callback': (errCode) => {
          if (widgetIdRef.current !== localId) return; // stale — ignore
          console.error(
            '[Turnstile] Challenge error:', errCode,
            '| retry', errorCount.current + 1, '/', MAX_ERROR_RETRIES
          );
          bufferedToken.current = null;
          errorCount.current += 1;
          setTurnstileInteractive(false);

          if (errorCount.current <= MAX_ERROR_RETRIES) {
            try { api.reset(localId); } catch (_) { /* ignore */ }
          } else {
            errorCount.current = 0;
            setTurnstileStatus('error');
            rejectAllPending(makeTurnstileError(`Turnstile challenge failed (code: ${errCode})`));
          }
        },

        'expired-callback': () => {
          if (widgetIdRef.current !== localId) return; // stale — ignore
          console.warn('[Turnstile] Token expired — widget will re-run challenge');
          bufferedToken.current = null;
          setTurnstileInteractive(false);
          setTurnstileStatus('pending');
          // Widget auto-resets; new token arrives via callback.
        },
      });

      widgetIdRef.current = localId;
    });
  }, [deliverToken, rejectAllPending]);

  // ── callback ref (attach to the container div with ref={turnstileRef}) ──────

  const turnstileRef = useCallback(
    (node) => {
      if (!node) {
        removeWidget();
        containerRef.current = null;
        return;
      }
      containerRef.current = node;
      if (!turnstileSiteKey) return;
      mountWidgetIfNeeded().catch((err) => {
        console.error('[Turnstile] Mount failed:', err.message);
        setTurnstileStatus('error');
      });
    },
    [removeWidget, mountWidgetIfNeeded]
  );

  // ── public API ──────────────────────────────────────────────────────────────

  /**
   * Reset and re-arm the widget so it produces a fresh token.
   * Call after every API request that consumed a token (tokens are one-use).
   */
  const resetTurnstile = useCallback(() => {
    bufferedToken.current = null;
    errorCount.current = 0;
    setTurnstileInteractive(false);
    const id = widgetIdRef.current;
    if (id != null && window.turnstile && typeof window.turnstile.reset === 'function') {
      try {
        window.turnstile.reset(id);
        setTurnstileStatus('pending');
      } catch (_) { /* ignore */ }
    }
  }, []);

  /**
   * Returns a Promise<string> with a valid Turnstile token.
   *
   * For normal traffic the token is already buffered (issued on mount).
   * For suspicious traffic Cloudflare shows its challenge; the promise
   * resolves once the visitor completes it.
   */
  const getTurnstileToken = useCallback(() => {
    if (!turnstileSiteKey) return Promise.resolve('');

    return mountWidgetIfNeeded().then(() => {
      // Happy path: token already buffered.
      if (bufferedToken.current) {
        const t = bufferedToken.current;
        bufferedToken.current = null;
        setTurnstileStatus('pending'); // consumed — widget will re-arm
        return t;
      }

      if (!containerRef.current) {
        return Promise.reject(makeTurnstileError('Turnstile container is not mounted'));
      }
      if (!widgetIdRef.current) {
        return Promise.reject(makeTurnstileError('Turnstile widget failed to render'));
      }

      // Widget is still running the challenge — wait for next token.
      return new Promise((resolve, reject) => {
        const entry = { resolve: null, reject: null, timeoutId: null };

        entry.timeoutId = window.setTimeout(() => {
          const idx = pendingRef.current.indexOf(entry);
          if (idx !== -1) pendingRef.current.splice(idx, 1);
          reject(makeTurnstileError('Turnstile timed out waiting for token'));
        }, 60_000);

        entry.resolve = (token) => {
          window.clearTimeout(entry.timeoutId);
          setTurnstileStatus('pending'); // consumed — widget will re-arm
          resolve(token);
        };
        entry.reject = (err) => {
          window.clearTimeout(entry.timeoutId);
          reject(err);
        };

        pendingRef.current.push(entry);

        // Race guard: callback may have fired between the check above and now.
        if (bufferedToken.current) {
          const t = bufferedToken.current;
          bufferedToken.current = null;
          const idx = pendingRef.current.indexOf(entry);
          if (idx !== -1) pendingRef.current.splice(idx, 1);
          window.clearTimeout(entry.timeoutId);
          setTurnstileStatus('pending');
          resolve(t);
        }
      });
    });
  }, [mountWidgetIfNeeded]);

  return {
    getTurnstileToken,
    resetTurnstile,
    turnstileRef,
    /** 'idle' | 'pending' | 'ready' | 'error' */
    turnstileStatus,
    turnstileInteractive,
    isTurnstileConfigured: Boolean(turnstileSiteKey),
  };
}
